import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Search } from '../../Search/Search'
import { FilterBarWrapper, FilterSearchContainer } from './FilterBar.styles'
import {
  AuditFilterDropdown,
  AuditFrameworksFilterDropdown,
  AuditStatusFilterDropdown,
  DepartmentFilterDropdown,
  HasDocumentationFilterDropdown,
  OwnerFilterDropdown,
  PolicyFilterDropdown,
  StatusFilterDropdown,
  OrganizationFilterDropdown,
  AssigneeFilterDropdown,
  QuestionnaireStatusFilterDropdown,
  QuestionnaireDateRangeFilter,
  AuditRunOwnerFilterDropdown,
  AuditRunControlFilterDropdown,
  AuditRunStatusFilterDropdown,
  AuditRunResultFilterDropdown,
  ReportCategoryFilterDropdown,
  ExecutionTypeFilterDropdown,
} from './FilterDropdowns'
import { ClearAllFilters } from './FilterBar.components'
import { FilterBarProps, FilterName, FilterParam } from './FilterBar.types'
import { FilterBarConfig } from './FilterBar.constants'
import { RiskFilterDropdown } from './FilterDropdowns/RiskFilterDropdown'
import { ReceptorFilterDropdown } from './FilterDropdowns/ReceptorFilterDropdown'
import { ServiceRoleFilterDropdown } from './FilterDropdowns/ServiceRoleFilterDropdown'
import { RiskTierFilterDropdown } from './FilterDropdowns/RiskTierFilterDropdown'
import { AttestationFilterDropdown } from './FilterDropdowns/AttestationFilterDropdown'
import { SmartChecksFilterDropdown } from './FilterDropdowns/SmartChecksFilterDropdown'
import { SourceFilterDropdown } from './FilterDropdowns/SourceFilterDropdown'
import { DateRangeFilter } from './DateRangeFilter'
import { ControlFilterDropdown } from './FilterDropdowns/ControlFilterDropdown'
import { EvidenceTypeFilterDropdown } from './FilterDropdowns/EvidenceTypeFilterDropdown'
import { ComplianceFrameworkFilterDropdown } from './FilterDropdowns/ComplianceFrameworkFilterDropdown'
import { TrustPortalRequestAccessDropdown } from './FilterDropdowns/TrustPortalRequestAccessDropdown'
import { TrustPortalPermissionDropdown } from './FilterDropdowns/TrustPortalPermissionDropdown'
import { CompanyFilterDropdown } from './FilterDropdowns/CompanyFilterDropdown'
import { ReportTypeFilterDropdown } from './FilterDropdowns/ReportTypeFilterDropdown'
import { TrustServiceCriteriaFilterDropdown } from './FilterDropdowns/TrustServiceCriteriaFilterDropdown'
import { AuditorFilterDropdown } from './FilterDropdowns/AuditorFilterDropdown'
import { ExpirationDateFilter } from './FilterDropdowns/ExpirationDateFilter'
import { UserRoleFilterDropdown } from './FilterDropdowns/UserRoleFilterDropdown'
import { EmailNotificationFilterDropdown } from './FilterDropdowns/EmailNotificationFilterDropdown'
import { MoreFiltersDropdown } from './MoreFiltersDropdown'
import { UserDepartmentFilterDropdown } from './FilterDropdowns/UserDepartmentFilterDropdown'
import { DashboardPolicyFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardPolicyFilterDropdown'
import { DashboardAuditFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardAuditFilterDropdown'
import { DashboardRiskFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardRiskFilterDropdown'
import { DashboardDepartmentFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardDepartmentFilterDropdown'
import { DashboardAssigneeFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardAssigneeFilterDropdown'
import { DashboardFrameworkFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardFrameworkFilterDropdown'
import { DashboardDateRangeFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown'
import { DashboardControlFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardControlFilterDropdown'
import { DashboardRiskCategoryFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardRiskCategoryFilterDropdown'
import { DashboardVendorFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardVendorFilterDropdown'
import { TrustPortalFeaturesDropdown } from './FilterDropdowns/TrustPortalFeaturesDropdown'
import { DashboardDocNameFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardDocNameFilterDropdown'
import { DashboardNameFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardNameFilterDropdown'
import { DashboardEmailFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardEmailFilterDropdown'
import { DashboardCompanyFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardCompanyFilterDropdown'
import { DashboardTPFeatureFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/TrustDimensionFilterDropdowns/DashboardTPFeatureFilterDropdown'
import { DashboardTPAccessLevelFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/TrustDimensionFilterDropdowns/DashboardTPAccessLevelFilterDropdown'
import { DashboardTPUpdateCategoryFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/TrustDimensionFilterDropdowns/DashboardTPUpdateCategoryFilterDropdown'

// Priority order for filters - higher priority filters will be shown first
// Typed from FilterBarConfig keys for type safety
const FILTER_PRIORITY: (keyof FilterBarConfig)[] = [
  'department',
  'status',
  'owner',
  'policy',
  'organization',
  'assignee',
  'questionnaireStatus',
  'questionnaireDateRange',
  'complianceFramework',
  'audit',
  'auditFrameworks',
  'risk',
  'receptor',
  'serviceRole',
  'riskTier',
  'attestation',
  'smartChecks',
  'source',
  'evidenceType',
  'dateRange',
  'control',
  'trustPortalRequestAccess',
  'trustPortalPermission',
  'trustPortalFeatures',
  'documentation',
  'audit_run_owner',
  'audit_run_control',
  'audit_run_status',
  'audit_run_result',
  'company',
  'reportType',
  'trustServiceCriteria',
  'auditor',
  'expirationDate',
  'userDepartment',
  'userRole',
  'emailNotification',
  'auditStatus',
  'dashboardFramework',
  'dashboardPolicy',
  'dashboardAudit',
  'dashboardRisk',
  'dashboardDepartment',
  'dashboardRiskCategory',
  'dashboardVendor',
  'dashboardControl',
  'dashboardAssignee',
  'dashboardDateRange',
  'reportCategories',
  'dashboardName',
  'dashboardEmail',
  'dashboardCompany',
  'dashboardDocName',
  'dashboardTPFeature',
  'dashboardTPAccessLevel',
  'dashboardTPUpdateCategory',
  'executionType',
]

export const FilterBar = ({
  config,
  searchBarOptions,
  resizeNodeId,
  customElement,
  dashboardContext,
  dashboardName,
}: FilterBarProps): JSX.Element => {
  const [visibleFilters, setVisibleFilters] = useState<
    (keyof FilterBarConfig)[]
  >([])
  const [hiddenFilters, setHiddenFilters] = useState<(keyof FilterBarConfig)[]>(
    [],
  )
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false)
  const filterBarRef = useRef<HTMLUListElement>(null)

  // Get all enabled filters based on config
  const getEnabledFilters = useCallback(() => {
    return FILTER_PRIORITY.filter(
      (filterKey) => config[filterKey as keyof typeof config],
    )
  }, [config])

  // Check if filters fit in the container
  const checkFiltersFit = useCallback(() => {
    if (!filterBarRef.current) return

    const container = filterBarRef.current
    const containerWidth = container.offsetWidth

    // Reserve space for "More filters" dropdown and some padding
    const reservedWidth = 140 + 16 // More filters button width + padding
    const reservedWidthPlusSearchWidth = reservedWidth + 741 // With search expanded

    const availableWidth = isSearchExpanded
      ? containerWidth - reservedWidthPlusSearchWidth
      : containerWidth - reservedWidth

    const enabledFilters = getEnabledFilters()
    let currentWidth = 80 // make room for 'Clear filters' button
    const newVisibleFilters: (keyof FilterBarConfig)[] = []
    const newHiddenFilters: (keyof FilterBarConfig)[] = []

    // Estimate width for each filter based on filter name length
    const getFilterWidth = (filterKey: keyof FilterBarConfig) => {
      const baseWidth = 80 // Base width for filter button
      const charWidth = 8 // Approximate width per character
      const filterName = getFilterDisplayName(filterKey)
      return baseWidth + filterName.length * charWidth + 8 // Add gap
    }

    for (const filterKey of enabledFilters) {
      const filterWidth = getFilterWidth(filterKey)
      currentWidth += filterWidth

      if (currentWidth <= availableWidth || !newVisibleFilters.length) {
        // we want at least one filter visible (other than 'more filters')
        newVisibleFilters.push(filterKey)
      } else {
        newHiddenFilters.push(filterKey)
      }
    }

    if (newHiddenFilters.length === 1) {
      // if there is only one hidden filter, show it in the visible filters
      newVisibleFilters.push(...newHiddenFilters)
      newHiddenFilters.pop()
    }

    setVisibleFilters(newVisibleFilters)
    setHiddenFilters(newHiddenFilters)
  }, [getEnabledFilters, isSearchExpanded])

  useEffect(() => {
    checkFiltersFit()

    const handleResize = () => {
      checkFiltersFit()
    }

    const node = resizeNodeId ? document.getElementById(resizeNodeId) : null
    const observer = new MutationObserver(handleResize)
    if (node) {
      observer.observe(node, {
        subtree: true,
        childList: true,
      })
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, [config, checkFiltersFit, isSearchExpanded, resizeNodeId])

  const renderFilter = useCallback(
    (filterKey: keyof FilterBarConfig, isNested = false) => {
      switch (filterKey) {
        case 'department':
          return (
            <DepartmentFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'policy':
          return <PolicyFilterDropdown key={filterKey} isNested={isNested} />
        case 'status':
          return <StatusFilterDropdown key={filterKey} isNested={isNested} />
        case 'owner':
          return <OwnerFilterDropdown key={filterKey} isNested={isNested} />
        case 'documentation':
          return (
            <React.Fragment key={filterKey}>
              <HasDocumentationFilterDropdown
                param={FilterParam.HAS_DOCUMENTATION}
                name={FilterName.HAS_DOCUMENTATION}
                isNested={isNested}
              />
              <HasDocumentationFilterDropdown
                param={FilterParam.IS_CUSTOMIZED}
                name={FilterName.IS_CUSTOMIZED}
                isNested={isNested}
              />
            </React.Fragment>
          )
        case 'complianceFramework':
          return (
            <ComplianceFrameworkFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'audit':
          return <AuditFilterDropdown key={filterKey} isNested={isNested} />
        case 'auditFrameworks':
          return (
            <AuditFrameworksFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'auditStatus':
          return (
            <AuditStatusFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'risk':
          return (
            <React.Fragment key={filterKey}>
              <RiskFilterDropdown
                param={FilterParam.INHERENT_RISK}
                name={FilterName.INHERENT_RISK}
                isNested={isNested}
              />
              <RiskFilterDropdown
                param={FilterParam.RESIDUAL_RISK}
                name={FilterName.RESIDUAL_RISK}
                isNested={isNested}
              />
            </React.Fragment>
          )
        case 'receptor':
          return <ReceptorFilterDropdown key={filterKey} isNested={isNested} />
        case 'serviceRole':
          return (
            <ServiceRoleFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'riskTier':
          return <RiskTierFilterDropdown key={filterKey} isNested={isNested} />
        case 'attestation':
          return (
            <AttestationFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'smartChecks':
          return (
            <SmartChecksFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'source':
          return <SourceFilterDropdown key={filterKey} isNested={isNested} />
        case 'evidenceType':
          return (
            <EvidenceTypeFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'dateRange':
          return <DateRangeFilter key={filterKey} isNested={isNested} />
        case 'questionnaireDateRange':
          return (
            <QuestionnaireDateRangeFilter key={filterKey} isNested={isNested} />
          )
        case 'control':
          return <ControlFilterDropdown key={filterKey} isNested={isNested} />
        case 'trustPortalRequestAccess':
          return (
            <TrustPortalRequestAccessDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'trustPortalPermission':
          return (
            <TrustPortalPermissionDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'trustPortalFeatures':
          return (
            <TrustPortalFeaturesDropdown key={filterKey} isNested={isNested} />
          )
        case 'organization':
          return (
            <OrganizationFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'assignee':
          return <AssigneeFilterDropdown key={filterKey} isNested={isNested} />
        case 'questionnaireStatus':
          return (
            <QuestionnaireStatusFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'audit_run_owner':
          return (
            <AuditRunOwnerFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'audit_run_control':
          return (
            <AuditRunControlFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'audit_run_status':
          return (
            <AuditRunStatusFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'audit_run_result':
          return (
            <AuditRunResultFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'company':
          return <CompanyFilterDropdown key={filterKey} isNested={isNested} />
        case 'reportType':
          return (
            <ReportTypeFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'trustServiceCriteria':
          return (
            <TrustServiceCriteriaFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'auditor':
          return <AuditorFilterDropdown key={filterKey} isNested={isNested} />
        case 'expirationDate':
          return <ExpirationDateFilter key={filterKey} isNested={isNested} />
        case 'userRole':
          return <UserRoleFilterDropdown key={filterKey} isNested={isNested} />
        case 'emailNotification':
          return (
            <EmailNotificationFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'userDepartment':
          return (
            <UserDepartmentFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'dashboardFramework':
          return <DashboardFrameworkFilterDropdown isNested={isNested} />
        case 'dashboardPolicy':
          return <DashboardPolicyFilterDropdown isNested={isNested} />
        case 'dashboardAudit':
          return <DashboardAuditFilterDropdown isNested={isNested} />
        case 'dashboardRisk':
          return <DashboardRiskFilterDropdown isNested={isNested} />
        case 'dashboardDepartment':
          return (
            <DashboardDepartmentFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardAssignee':
          return <DashboardAssigneeFilterDropdown isNested={isNested} />
        case 'dashboardDateRange':
          return <DashboardDateRangeFilterDropdown />
        case 'dashboardControl':
          return <DashboardControlFilterDropdown isNested={isNested} />
        case 'dashboardRiskCategory':
          return <DashboardRiskCategoryFilterDropdown isNested={isNested} />
        case 'dashboardVendor':
          return <DashboardVendorFilterDropdown isNested={isNested} />
        case 'reportCategories':
          return <ReportCategoryFilterDropdown isNested={isNested} />
        case 'dashboardName':
          return (
            <DashboardNameFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardEmail':
          return (
            <DashboardEmailFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardCompany':
          return (
            <DashboardCompanyFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardDocName':
          return (
            <DashboardDocNameFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardTPFeature':
          return <DashboardTPFeatureFilterDropdown isNested={isNested} />
        case 'dashboardTPAccessLevel':
          return <DashboardTPAccessLevelFilterDropdown isNested={isNested} />
        case 'dashboardTPUpdateCategory':
          return (
            <DashboardTPUpdateCategoryFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'executionType':
          return <ExecutionTypeFilterDropdown isNested={isNested} />
        default:
          return null
      }
    },
    [dashboardContext, dashboardName],
  )

  const hiddenFiltersElements = useMemo(
    () =>
      hiddenFilters.map((filterKey) => ({
        id: getFilterParamFromKey(filterKey),
        element: renderFilter(filterKey, true),
      })),
    [hiddenFilters, renderFilter],
  )

  const visibleFiltersElements = useMemo(
    () => visibleFilters.map((filterKey) => renderFilter(filterKey)),
    [visibleFilters, renderFilter],
  )

  return (
    <FilterSearchContainer
      $isSearchExpanded={isSearchExpanded}
      ref={filterBarRef}
    >
      <FilterBarWrapper>
        {visibleFiltersElements}
        {hiddenFilters.length > 0 && (
          <MoreFiltersDropdown filters={hiddenFiltersElements} />
        )}
        <ClearAllFilters />
      </FilterBarWrapper>
      {searchBarOptions && (
        <Search
          setIsExpanded={setIsSearchExpanded}
          isExpanded={isSearchExpanded}
          withContentSearch={searchBarOptions.withContentSearch}
          withAdvancedSearch
        />
      )}
      {customElement}
    </FilterSearchContainer>
  )
}

// Get display name for filter
const getFilterDisplayName = (filterKey: keyof FilterBarConfig) => {
  switch (filterKey) {
    case 'department':
      return FilterName.DEPARTMENT
    case 'policy':
      return FilterName.POLICY
    case 'status':
      return FilterName.STATUS
    case 'owner':
      return FilterName.OWNER
    case 'organization':
      return FilterName.ORGANIZATION
    case 'assignee':
      return FilterName.ASSIGNEE
    case 'questionnaireStatus':
      return FilterName.QUESTIONNAIRE_STATUS
    case 'documentation':
      return FilterName.HAS_DOCUMENTATION
    case 'complianceFramework':
      return FilterName.COMPLIANCE_FRAMEWORK
    case 'audit':
      return FilterName.AUDIT
    case 'auditFrameworks':
      return FilterName.AUDIT_FRAMEWORKS
    case 'risk':
      return FilterName.INHERENT_RISK
    case 'receptor':
      return FilterName.RECEPTOR
    case 'serviceRole':
      return FilterName.SERVICE_ROLE
    case 'riskTier':
      return FilterName.RISK_TIER
    case 'attestation':
      return FilterName.ATTESTATION
    case 'smartChecks':
      return FilterName.AI_CONTROL_CHECKS
    case 'source':
      return FilterName.SOURCE
    case 'evidenceType':
      return FilterName.EVIDENCE_TYPE
    case 'dateRange':
      return 'Date Range'
    case 'questionnaireDateRange':
      return 'Due Date'
    case 'control':
      return 'Control'
    case 'trustPortalRequestAccess':
      return FilterName.TRUST_PORTAL_REQUEST_ACCESS
    case 'trustPortalPermission':
      return FilterName.TRUST_PORTAL_PERMISSION
    case 'trustPortalFeatures':
      return FilterName.TRUST_PORTAL_FEATURES
    case 'audit_run_owner':
      return FilterName.AUDIT_RUN_OWNER
    case 'audit_run_control':
      return FilterName.AUDIT_RUN_CONTROL
    case 'audit_run_status':
      return FilterName.AUDIT_RUN_STATUS
    case 'audit_run_result':
      return FilterName.AUDIT_RUN_RESULT
    case 'company':
      return FilterName.COMPANY
    case 'reportType':
      return FilterName.REPORT_TYPE
    case 'trustServiceCriteria':
      return FilterName.TRUST_SERVICE_CRITERIA
    case 'auditor':
      return FilterName.AUDITOR
    case 'expirationDate':
      return FilterName.EXPIRATION_DATE
    case 'userRole':
      return FilterName.USER_ROLE
    case 'emailNotification':
      return FilterName.EMAIL_NOTIFICATION
    case 'userDepartment':
      return FilterName.USER_DEPARTMENT
    case 'dashboardFramework':
      return FilterName.DASHBOARD_FRAMEWORK
    case 'dashboardPolicy':
      return FilterName.DASHBOARD_POLICY
    case 'dashboardAudit':
      return FilterName.DASHBOARD_AUDIT
    case 'dashboardRisk':
      return FilterName.DASHBOARD_RISK
    case 'dashboardDepartment':
      return FilterName.DASHBOARD_DEPARTMENT
    case 'dashboardAssignee':
      return FilterName.DASHBOARD_ASSIGNEE
    case 'dashboardDateRange':
      return FilterName.DASHBOARD_DATE_RANGE
    case 'executionType':
      return FilterName.EXECUTION_TYPE
    default:
      return filterKey
  }
}

// Map config keys to FilterParam values
const getFilterParamFromKey = (
  filterKey: keyof FilterBarConfig,
): string | string[] => {
  switch (filterKey) {
    case 'department':
      return FilterParam.DEPARTMENT
    case 'policy':
      return FilterParam.POLICY
    case 'status':
      return FilterParam.STATUS
    case 'owner':
      return FilterParam.OWNER
    case 'organization':
      return FilterParam.ORGANIZATION
    case 'assignee':
      return FilterParam.ASSIGNEE
    case 'questionnaireStatus':
      return FilterParam.QUESTIONNAIRE_STATUS
    case 'documentation':
      return [FilterParam.HAS_DOCUMENTATION, FilterParam.IS_CUSTOMIZED]
    case 'complianceFramework':
      return FilterParam.COMPLIANCE_FRAMEWORK
    case 'audit':
      return FilterParam.AUDIT
    case 'auditFrameworks':
      return FilterParam.AUDIT_FRAMEWORKS
    case 'risk':
      return [FilterParam.INHERENT_RISK, FilterParam.RESIDUAL_RISK]
    case 'receptor':
      return FilterParam.RECEPTOR
    case 'serviceRole':
      return FilterParam.SERVICE_ROLE
    case 'riskTier':
      return FilterParam.RISK_TIER
    case 'attestation':
      return FilterParam.ATTESTATION
    case 'smartChecks':
      return FilterParam.AI_CONTROL_CHECKS
    case 'source':
      return FilterParam.SOURCE
    case 'evidenceType':
      return FilterParam.EVIDENCE_TYPE
    case 'dateRange':
      return FilterParam.DATE_RANGE
    case 'questionnaireDateRange':
      return FilterParam.DATE_RANGE
    case 'control':
      return FilterParam.CONTROL
    case 'trustPortalRequestAccess':
      return FilterParam.TRUST_PORTAL_REQUEST_ACCESS
    case 'trustPortalPermission':
      return FilterParam.TRUST_PORTAL_PERMISSION
    case 'trustPortalFeatures':
      return FilterParam.TRUST_PORTAL_FEATURES
    case 'audit_run_owner':
      return FilterParam.AUDIT_RUN_OWNER
    case 'audit_run_control':
      return FilterParam.AUDIT_RUN_CONTROL
    case 'audit_run_status':
      return FilterParam.AUDIT_RUN_STATUS
    case 'audit_run_result':
      return FilterParam.AUDIT_RUN_RESULT
    case 'company':
      return FilterParam.COMPANY
    case 'reportType':
      return FilterParam.REPORT_TYPE
    case 'trustServiceCriteria':
      return FilterParam.TRUST_SERVICE_CRITERIA
    case 'auditor':
      return FilterParam.AUDITOR
    case 'expirationDate':
      return FilterParam.DATE_RANGE
    case 'userRole':
      return FilterParam.USER_ROLE
    case 'emailNotification':
      return FilterParam.EMAIL_NOTIFICATION
    case 'userDepartment':
      return FilterParam.USER_DEPARTMENT
    case 'executionType':
      return FilterParam.EXECUTION_TYPE
    default:
      return filterKey
  }
}
